import dayjs from "dayjs";
import { MetricSource, MetricType } from "./Metrics.types";
import { Metric as MetricData } from "@/entities/Finance/Finance.model";
import { Period } from "./Period";
import { PeriodRange } from "./PeriodRange";
import { METRIC_CONFIG } from "./METRIC_CONFIG";
import { PeriodResolution } from "./Metrics.types";
import { HydratedValue } from "./HydratedValue";
import { AccountLabel } from "@/entities/Labeling/Account.model";
import { FinanceLabField } from "@/entities/FinanceLab/FinanceLab.model";

export type HydratedMetricValue = {
    period: Period;
    value: HydratedValue;
};

export type HydratedMetricValues = HydratedMetricValue[];

export type HydratedMetricObject = Record<string, HydratedValue> & {
    code: AccountLabel | FinanceLabField;
    label: string;
    source: MetricSource;
    type: MetricType;
    periodRange: PeriodRange | null;
    rawData: MetricData;
};

// HydratedMetrics assume a plain view of the data, i.e. no lineage
export class HydratedMetric {
    readonly rawData: MetricData;
    readonly values: HydratedMetricValues;
    readonly type: MetricType;
    readonly periodRange: PeriodRange | null;
    readonly label: string;
    readonly resolution: PeriodResolution;
    readonly source: MetricSource;
    readonly code: AccountLabel | FinanceLabField;

    constructor(data: MetricData, resolution: PeriodResolution) {
        this.rawData = data;
        this.resolution = resolution;
        const config = METRIC_CONFIG[data.info.derived.label];
        if (!config) {
            throw new Error(
                `Metric config not found for label: ${data.info.derived.label}`,
            );
        }
        this.type = config.type;
        this.label = config.label;
        this.source = config.source;
        this.code = data.info.derived.label as AccountLabel | FinanceLabField;
        this.values = data.values.map((value) => ({
            period: new Period(value.period),
            value: new HydratedValue(value.value, this.type),
        }));
        this.periodRange = PeriodRange.fromArray(
            this.values.map(({ period }) => period),
        );
    }

    getValuesForPeriods(periods: Period[]): HydratedMetricValues {
        const valueMap = new Map(
            this.values.map((v) => [
                v.period.base.startOf("day").valueOf(),
                v.value.number,
            ]),
        );
        return periods.map((period) => ({
            period,
            value: new HydratedValue(
                valueMap.get(period.base.startOf("day").valueOf()) ?? null,
                this.type,
            ),
        }));
    }

    getLastNPeriods(n: number): HydratedMetricValues {
        if (!this.periodRange) return [];
        const periods = this.periodRange
            .getAllPeriods(this.resolution)
            .slice(-n);
        return this.getValuesForPeriods(periods);
    }

    sumLastNPeriods(n: number): number {
        return this.getLastNPeriods(n).reduce(
            (acc, curr) => acc + curr.value.getValueOrZero(),
            0,
        );
    }

    getValuesAsOf(asOfDate: dayjs.Dayjs): HydratedMetricValues {
        if (!this.periodRange) return [];
        const periods = this.periodRange.getPeriodsAsOf(
            new Period(asOfDate),
            this.resolution,
        );
        return this.getValuesForPeriods(periods);
    }

    getPeriodsForRange(range: PeriodRange | null): HydratedMetricValues {
        if (!this.periodRange || !range) return [];
        const periods = this.periodRange
            .getAllPeriods(this.resolution)
            .filter((period) => range.includes(period));
        return this.getValuesForPeriods(periods);
    }

    getPeriodObject(): Record<string, HydratedValue> {
        return this.values.reduce((acc, { period, value }) => {
            acc[period.getShortName(this.resolution)] = value;
            return acc;
        }, {});
    }

    getLTMValue(asOfDate?: dayjs.Dayjs): HydratedValue {
        if (this.resolution !== PeriodResolution.MONTHLY) {
            return new HydratedValue(null, this.type);
        }

        const date = asOfDate || dayjs();
        const isCompleteMonth = date.isSame(date.endOf("month"), "day");
        const values = isCompleteMonth
            ? this.getLastNPeriods(12)
            : this.getLastNPeriods(13).slice(0, -1);

        if (values.length < 12 || values.some((v) => v.value.isNull())) {
            return new HydratedValue(null, this.type);
        }

        const sum = values.reduce(
            (acc, curr) => acc + curr.value.getValueOrZero(),
            0,
        );
        return new HydratedValue(
            this.type === MetricType.PERCENTAGE ? sum / 12 : sum,
            this.type,
        );
    }

    getLTMFormula(asOfDate?: dayjs.Dayjs): {
        formula: string[];
        total: string;
    } | null {
        if (this.resolution !== PeriodResolution.MONTHLY) {
            return null;
        }

        const date = asOfDate || dayjs();
        const isCompleteMonth = date.isSame(date.endOf("month"), "day");
        const values = isCompleteMonth
            ? this.getLastNPeriods(12)
            : this.getLastNPeriods(13).slice(0, -1);

        if (values.length < 12) {
            return null;
        }

        const formatOrMissing = (v: HydratedMetricValue) =>
            v.value.isNull()
                ? `${v.period.getFullName(this.resolution)} (Missing)`
                : v.value.getFormattedValue();

        const hasMissing = values.some((v) => v.value.isNull());
        const formula = values
            .map(formatOrMissing)
            .flatMap((v, i) => (i === 0 ? [v] : ["+", v]));

        const sum = values.reduce(
            (acc, curr) => acc + curr.value.getValueOrZero(),
            0,
        );
        const result = this.type === MetricType.PERCENTAGE ? sum / 12 : sum;

        return {
            formula:
                this.type === MetricType.PERCENTAGE
                    ? ["(", ...formula, ")", "/", "12"]
                    : formula,
            total: hasMissing
                ? "-"
                : new HydratedValue(result, this.type).getFormattedValue(),
        };
    }

    toTabular(): HydratedMetricObject {
        return {
            code: this.code,
            label: this.label,
            source: this.source,
            type: this.type,
            periodRange: this.periodRange,
            rawData: this.rawData,
            ...this.getPeriodObject(),
        } as HydratedMetricObject;
    }
}
